import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import RequestTable from './sections/RequestTable/RequestTable';
import PrimaryHero from '../../../shared/components/v3/Heros/PrimaryHero/PrimaryHero';
import BdsModal from '../../../shared/components/v3/Modals/BdsModal/BdsModal';
import Toast from '../../../shared/components/v3/Toast/Toast';
import { HOME_ROUTE } from '../../../shared/constants/auth';
import {
  DEFAULT_BDS_MODAL,
  REQUEST_ACCUMULATION_LIMIT_EXCEDEED_MODAL,
  REQUEST_INTERNAL_FLEXIBLE_REMUNERATION_ERROR_MODAL,
  REQUEST_SALARY_PERCENTAGE_EXCEDEED_MODAL,
  REQUEST_SMI_THRESHOLD_VIOLATION_MODAL
} from '../../../shared/constants/modal';
import { PLAN_TYPE_FR } from '../../../shared/constants/plan';
import { FLEXIBLE_REMUNERATION_REJECT_REASONS } from '../../../shared/constants/requests';
import { GENERIC_ERROR_TOAST } from '../../../shared/constants/toast';
import { PlansContext } from '../../../shared/context/plans/plansContext';
import { IBdsModalContent } from '../../../shared/interfaces/bdsModal';
import { IToast } from '../../../shared/interfaces/toast';
import { TRANSLATIONS_REQUESTS } from '../../../shared/translations/requests';
import './RequestsTable.scss';

type LocationState = { toast?: IToast; errorTag?: string };

const RequestsTable = () => {
  const [openToast, setOpenToast] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [toastData, setToastData] = useState(GENERIC_ERROR_TOAST);
  const [modalContent, setModalContent] = useState<IBdsModalContent>(DEFAULT_BDS_MODAL);

  const { show } = useIntercom();

  const openIntercom = () => {
    show();
    setOpenModal(false);
  };

  const getModalContent = useCallback((errorTag?: string) => {
    switch (errorTag) {
      case FLEXIBLE_REMUNERATION_REJECT_REASONS.salaryPercentageExceeded:
        return REQUEST_SALARY_PERCENTAGE_EXCEDEED_MODAL;
      case FLEXIBLE_REMUNERATION_REJECT_REASONS.accumulationLimitExceeded:
        return REQUEST_ACCUMULATION_LIMIT_EXCEDEED_MODAL;
      case FLEXIBLE_REMUNERATION_REJECT_REASONS.smiThresholdViolation:
        return REQUEST_SMI_THRESHOLD_VIOLATION_MODAL;
      default:
        return REQUEST_INTERNAL_FLEXIBLE_REMUNERATION_ERROR_MODAL;
    }
  }, []);

  const { t } = useTranslation();

  const { planType, loading } = useContext(PlansContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location as { state: LocationState | null };

  const handleCloseToast = () => {
    setOpenToast(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    if (!loading && planType !== PLAN_TYPE_FR) {
      navigate(HOME_ROUTE);
    }
  }, [planType, loading, navigate]);

  useEffect(() => {
    if (state?.toast) {
      setToastData(state.toast);
      setOpenToast(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (state?.errorTag) {
      const selectedModal = getModalContent(state.errorTag);
      setModalContent(selectedModal);
      setOpenModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [state, navigate, location.pathname, getModalContent]);

  return (
    <>
      <div className="request-table-container">
        <PrimaryHero title={t(TRANSLATIONS_REQUESTS.table.hero.title)} color="hero-requests" />

        <div className="table-container">
          <RequestTable />
        </div>

        <BdsModal
          reverseButtons={!!modalContent.secondaryActionText}
          open={openModal}
          title={t(modalContent.title as string)}
          description={t(modalContent.description as string)}
          icon={<modalContent.icon />}
          primaryAction={{
            label: t(modalContent.primaryActionText as string),
            onClick: modalContent.secondaryActionText ? openIntercom : handleCloseModal,
            disabled: loading
          }}
          secondaryAction={
            modalContent.secondaryActionText
              ? {
                  label: t(modalContent.secondaryActionText),
                  onClick: handleCloseModal,
                  disabled: loading
                }
              : undefined
          }
          loading={loading}
        />
      </div>
      <Toast
        open={openToast}
        handleClose={handleCloseToast}
        buttonAction={handleCloseToast}
        title={t(toastData.title)}
        description={t(toastData.description)}
        icon={toastData.icon}
        button={toastData.button}
      />
    </>
  );
};

export default RequestsTable;
